import {
  AdminApiError,
  type AdminClient,
  type GgrReport,
  type PerGameReport,
  type PlaysReport,
  type TimeseriesReport,
} from '@apx/admin-sdk';
import { exportXlsx, exportXlsxWorkbook, type XlsxSheet } from '../xlsx.js';
import { fmtDate, fmtDateTime, fmtMoney, fmtTime } from '../format.js';
import { toDecimalString } from '@apx/money';
import { skeletonTable } from '../skeleton.js';
import { showToast } from '../toast.js';
import {
  mountAutoplay,
  mountRecentBets,
  mountPerGame,
  mountRecentWins,
  mountTopBettors,
} from './reports-tables.js';
import { renderRetentionTab } from './reports-retention.js';
import { renderReconcileTab } from './reports-reconcile.js';
import {
  acceptStatusCell,
  avgCredits,
  BASIS_HINT,
  BASIS_LABEL,
  deltaChip,
  escapeHtml,
  money,
  parseBasis,
  pct,
  REPORT_BASIS_STORAGE_KEY,
  REPORT_TZ_STORAGE_KEY,
  resolveBrowserTz,
  SINCE_LABEL,
  SINCE_MS,
  tzSelectOptions,
  txIdCell,
  type Since,
  wireTxIdCopy,
  zonedWallClockToUTC,
} from './reports-shared.js';
import type { ReportBasis } from '@apx/admin-sdk';

import { wireDatePickers } from '../date-picker.js';

export type ReportTab = 'pl' | 'product-pl' | 'player-pl' | 'game-log' | 'retention' | 'reconcile';

export interface ReportsPageDeps {
  client: AdminClient;
  integratorId: string;
  tab?: ReportTab;
}

const TAB_META: Record<'pl' | 'game-log' | 'retention' | 'reconcile', { title: string; sub: string }> = {
  pl: {
    title: 'P/L report',
    sub: 'Turnover, payout, GGR, RTP and player economics over the bet ledger — with a per-game breakdown, top players, and an hourly/daily trend.',
  },
  'game-log': {
    title: 'Player game log',
    sub: 'Every bet in the window, recent settled big-win rounds, and auto-play session rollups. Transaction-level audit trail. Rolled-back bets are listed too — Accept status says whether the player undid it (cancelled) or an admin refunded it (confirmed); the rollups above exclude both from their totals.',
  },
  retention: {
    title: 'Retention',
    sub: 'Daily active players split into new vs returning, plus D1/D7 cohort return rates, per game.',
  },
  reconcile: {
    title: 'Reconcile',
    sub: 'Paste an integrator’s transaction id list and compare it against apx for the same window — matched, missing on either side, and transactions both systems hold but date differently.',
  },
};

export async function renderReportsPage(root: HTMLElement, deps: ReportsPageDeps): Promise<void> {
  // Product P/L + Player P/L were folded into P/L — old deep-links still resolve.
  const raw = deps.tab ?? 'pl';
  const tab: 'pl' | 'game-log' | 'retention' | 'reconcile' =
    raw === 'product-pl' || raw === 'player-pl'
      ? 'pl'
      : (raw as 'pl' | 'game-log' | 'retention' | 'reconcile');
  const meta = TAB_META[tab];
  const ctx = { integratorId: deps.integratorId };

  // Game list powers the dropdown (no more typing exact ids). Fail-soft to an
  // empty list → the filter shows "All games" only.
  const games = await loadGames(deps);

  if (tab === 'retention') {
    await renderRetentionTab(root, deps, meta, games);
    return;
  }

  if (tab === 'reconcile') {
    await renderReconcileTab(root, deps, meta, games);
    return;
  }

  // Currency filter — every money metric is per-currency now (native model:
  // no credit unit, and turnover/GGR/RTP can't be summed across currencies).
  // The report scopes to one of the integrator's enabled currencies; default
  // to the first. Fail-soft to empty → the report runs unfiltered (blended),
  // which only happens for an integrator with no enabled currency.
  const currencies = await loadCurrencies(deps);
  let currency: string | undefined = currencies[0];
  // "All currencies" — empty value maps to undefined, which omits the server
  // filter. Defaults to the first currency; the all-currency view is opt-in and
  // renders per-currency. Offered only when more than one currency exists.
  const currencyOptions = (currencies.length > 1 ? ['<option value="">All currencies</option>'] : [])
    .concat(
      currencies.map(
        (c) => `<option value="${escapeHtml(c)}"${c === currency ? ' selected' : ''}>${escapeHtml(c)}</option>`,
      ),
    )
    .join('');

  const isPl = tab === 'pl';
  const isLog = tab === 'game-log';

  const gameOptions = ['<option value="">All games</option>']
    .concat(games.map((g) => `<option value="${escapeHtml(g)}">${escapeHtml(g)}</option>`))
    .join('');

  // Timezone filter — apx stores placedAt/settledAt in UTC; the report "day"
  // needs to match whatever tz the operator is reconciling against (e.g.
  // shortie closes its day at GMT+8). Sticky across reloads via localStorage.
  const browserTz = resolveBrowserTz();
  const storedTz = localStorage.getItem(REPORT_TZ_STORAGE_KEY);
  const tzOptions = tzSelectOptions(browserTz);
  const initialTz = storedTz && tzOptions.some((o) => o.value === storedTz) ? storedTz : browserTz;
  const tzOptionsHtml = tzOptions
    .map((o) => `<option value="${escapeHtml(o.value)}"${o.value === initialTz ? ' selected' : ''}>${escapeHtml(o.label)}</option>`)
    .join('');

  // Basis filter — settle time by default so a boundary bet lands on the same
  // day here as it does in the integrator's own transaction list. Sticky like
  // the tz; see REPORT_BASIS_STORAGE_KEY.
  const initialBasis = parseBasis(localStorage.getItem(REPORT_BASIS_STORAGE_KEY));
  const basisOptionsHtml = (['settled', 'placed'] as const)
    .map((b) => `<option value="${b}"${b === initialBasis ? ' selected' : ''}>${escapeHtml(BASIS_LABEL[b])}</option>`)
    .join('');

  root.innerHTML = `
    <h1 class="page-title">${escapeHtml(meta.title)}</h1>
    <p class="page-sub">${escapeHtml(meta.sub)}</p>

    <div class="card report-filters">
      <div class="filter-row">
        <label class="filter-field">Game
          <select id="r-game" class="field-input">${gameOptions}</select>
        </label>
        <label class="filter-field">Window
          <select id="r-since" class="field-input">
            <option value="1h">Last hour</option>
            <option value="24h" selected>Last 24 hours</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="custom">Custom range…</option>
          </select>
        </label>
        ${isPl ? `<label class="filter-field filter-field--search">Player
          <input id="r-player" type="text" class="field-input" placeholder="Search id, integrator ref, or username…" />
        </label>` : ''}
        <label class="filter-field filter-field--search">Transaction ID
          <input id="r-q" type="text" class="field-input" placeholder="Paste an id from their report…" />
        </label>
        ${currencies.length > 0 ? `<label class="filter-field">Currency
          <select id="r-currency" class="field-input">${currencyOptions}</select>
        </label>` : ''}
        <label class="filter-field">Count by
          <select id="r-basis" class="field-input">${basisOptionsHtml}</select>
        </label>
        <label class="filter-field">Timezone
          <select id="r-tz" class="field-input">${tzOptionsHtml}</select>
        </label>
        <span id="r-custom-range" class="filter-field-group hidden">
          <span class="filter-field"><label for="r-from">From</label><input id="r-from" type="datetime-local" step="1" class="field-input" /></span>
          <span class="filter-field"><label for="r-to">To</label><input id="r-to" type="datetime-local" step="1" class="field-input" /></span>
        </span>
        <button id="r-run" class="primary">Refresh</button>
        <button id="r-export" class="ghost">Export Excel</button>
        <span id="r-msg" class="muted filter-msg"></span>
      </div>
      <div id="r-range" class="filter-range muted"></div>
    </div>

    ${isPl ? `<div id="r-kpis"></div>` : ''}
    ${isPl ? `<div id="r-card-per-game" class="card"><h2>Per-game breakdown</h2><div id="r-per-game">${skeletonTable({ rows: 3, columns: 6 })}</div></div>` : ''}
    ${isPl ? `<div id="r-card-bettors" class="card"><h2>Top players <span class="muted section-hint">by turnover</span></h2><div id="r-bettors">${skeletonTable({ rows: 5, columns: 6 })}</div></div>` : ''}
    ${isPl ? `<div id="r-card-timeseries" class="card"><h2>Trend</h2><div id="r-timeseries">${skeletonTable({ rows: 4, columns: 4 })}</div></div>` : ''}
    ${isPl ? `<div id="r-card-plays" class="card"><h2 id="r-plays-h2">Plays <span class="muted section-hint">bet list</span></h2><div id="r-plays">${skeletonTable({ rows: 8, columns: 13 })}</div></div>` : ''}

    ${isLog ? `<div class="card"><h2>Recent plays <span class="muted section-hint">every bet, all outcomes, latest first · amounts in each bet’s currency</span></h2><div id="r-plays">${skeletonTable({ rows: 8, columns: 12 })}</div></div>` : ''}
    ${isLog ? `<div class="card"><h2>Recent big wins <span class="muted section-hint">latest 50</span></h2><div id="r-wins">${skeletonTable({ rows: 6, columns: 10 })}</div></div>` : ''}
    ${
      isLog
        ? `<div class="card">
            <h2>Auto-play sessions <span class="muted section-hint">latest 100</span></h2>
            <p class="muted section-desc">Grouped by <code>meta.sessionId</code> — one row per session: player, target slot, rounds, staked, payout, net.</p>
            <div id="r-autoplay">${skeletonTable({ rows: 5, columns: 9 })}</div>
          </div>`
        : ''
    }
  `;

  const kpisEl = root.querySelector<HTMLElement>('#r-kpis');
  const perGameEl = root.querySelector<HTMLElement>('#r-per-game');
  const bettorsEl = root.querySelector<HTMLElement>('#r-bettors');
  const timeseriesEl = root.querySelector<HTMLElement>('#r-timeseries');
  const playsEl = root.querySelector<HTMLElement>('#r-plays');
  const winsEl = root.querySelector<HTMLElement>('#r-wins');
  const autoplayEl = root.querySelector<HTMLElement>('#r-autoplay');
  const msgEl = root.querySelector<HTMLElement>('#r-msg')!;
  const rangeEl = root.querySelector<HTMLElement>('#r-range')!;

  // Delegated once on the page root, so both survive every table re-render.
  wireTxIdCopy(root, (id) => showToast(`Copied ${id}`, 'success'));
  wireDatePickers(root);

  // Card wrappers (not just the inner #r-* content divs) — player-focused
  // mode hides Top players/Trend and moves the Plays card up under the KPIs.
  const perGameCard = root.querySelector<HTMLElement>('#r-card-per-game');
  const bettorsCard = root.querySelector<HTMLElement>('#r-card-bettors');
  const timeseriesCard = root.querySelector<HTMLElement>('#r-card-timeseries');
  const playsCard = root.querySelector<HTMLElement>('#r-card-plays');
  const playsHeading = root.querySelector<HTMLElement>('#r-plays-h2');

  /**
   * Flip the P/L layout into "player-focused" mode when a player search is
   * active: KPIs + per-game stay (now player-scoped), Plays moves directly
   * under the KPIs (above per-game) and becomes the highlight, and
   * Top-players/Trend — meaningless for a single player — are hidden.
   *
   * Recomputed from scratch on every call (not just once) since `run()` can
   * fire repeatedly (Refresh, Enter in the player field, tab re-renders) —
   * `after()` is idempotent regardless of the card's current DOM position,
   * so this is safe to call unconditionally each render.
   */
  function applyPlayerModeLayout(playerMode: boolean): void {
    if (!isPl || !kpisEl || !perGameCard || !playsCard) return;
    bettorsCard?.classList.toggle('hidden', playerMode);
    timeseriesCard?.classList.toggle('hidden', playerMode);
    if (playerMode) {
      kpisEl.after(playsCard);
      if (playsHeading) playsHeading.innerHTML = 'Plays';
    } else {
      // Restore the original bottom-of-page position: right after Trend.
      (timeseriesCard ?? perGameCard).after(playsCard);
      if (playsHeading) playsHeading.innerHTML = 'Plays <span class="muted section-hint">bet list</span>';
    }
  }

  // Current filter args — used by the server-paged tables + CSV export.
  let lastArgs: { gameId?: string; since: Since; from?: string; to?: string; player?: string; currency?: string; basis: ReportBasis; q?: string } | null = null;
  let cache: {
    ggr: GgrReport | null;
    priorGgr: GgrReport | null;
    perGame: PerGameReport | null;
    ts: TimeseriesReport | null;
    plays: PlaysReport | null;
    gameId?: string;
  } | null = null;

  function readControls(): {
    gameId?: string;
    since: Since;
    from?: string;
    to?: string;
    tz: string;
    basis: ReportBasis;
    player?: string;
    q?: string;
    error?: string;
  } {
    const gameId = root.querySelector<HTMLSelectElement>('#r-game')!.value || undefined;
    const sel = root.querySelector<HTMLSelectElement>('#r-since')!.value;
    const tz = root.querySelector<HTMLSelectElement>('#r-tz')!.value || browserTz;
    const basis = parseBasis(root.querySelector<HTMLSelectElement>('#r-basis')?.value);
    const player = root.querySelector<HTMLInputElement>('#r-player')?.value.trim() || undefined;
    const q = root.querySelector<HTMLInputElement>('#r-q')?.value.trim() || undefined;
    if (sel === 'custom') {
      const fromRaw = root.querySelector<HTMLInputElement>('#r-from')!.value;
      const toRaw = root.querySelector<HTMLInputElement>('#r-to')!.value;
      if (!fromRaw || !toRaw) return { since: '24h', gameId, tz, basis, player, error: 'Pick both From and To for a custom range.' };
      // Interpret the wall-clock datetime-local values in the SELECTED tz (not
      // the browser's) so a custom range lines up with the tz-bucketed trend.
      const from = zonedWallClockToUTC(fromRaw, tz);
      const to = zonedWallClockToUTC(toRaw, tz);
      if (from >= to) return { since: '24h', gameId, tz, basis, player, error: 'From must be before To.' };
      return { gameId, since: '24h', from: from.toISOString(), to: to.toISOString(), tz, basis, player, q };
    }
    if (sel === 'today' || sel === 'yesterday') {
      // Full calendar day in the SELECTED tz → [00:00:00, 23:59:59]. The backend's
      // exclusive next-second bound turns To=23:59:59 into `< next-midnight`, so the
      // whole day is covered and boundary bets land on the right day (matching
      // shortie's calendar-day bucketing). Sent as from/to; `since` is a placeholder.
      const dayStr = new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(
        new Date(Date.now() - (sel === 'yesterday' ? 86_400_000 : 0)),
      );
      const from = zonedWallClockToUTC(`${dayStr}T00:00:00`, tz);
      const to = zonedWallClockToUTC(`${dayStr}T23:59:59`, tz);
      return { gameId, since: '24h', from: from.toISOString(), to: to.toISOString(), tz, basis, player, q };
    }
    return { gameId, since: sel as Since, tz, basis, player, q };
  }

  function repaintPl(): void {
    if (!cache) return;
    const { since, tz, player } = readControls();
    applyPlayerModeLayout(!!player);
    if (kpisEl && cache.ggr) kpisEl.innerHTML = renderKpis(cache.ggr, cache.priorGgr, currency ?? soleCurrency(cache.ggr));
    if (perGameEl && cache.perGame) {
      // Client-side game filter (the per-game endpoint returns all games).
      const filtered = cache.gameId
        ? cache.perGame.games.filter((g) => g.gameId === cache!.gameId)
        : cache.perGame.games;
      mountPerGame(perGameEl, filtered, {
        currency: currency ?? soleCurrency(cache.ggr) ?? '',
        integratorId: deps.integratorId,
      });
    }
    if (bettorsEl && lastArgs) {
      const a = lastArgs;
      mountTopBettors(bettorsEl, { currency: currency ?? '', integratorId: deps.integratorId }, (page, limit) =>
        deps.client.fetchTopBettors(deps.integratorId, { ...a, page, limit }),
      );
    }
    if (timeseriesEl && cache.ts) timeseriesEl.innerHTML = renderTimeseries(cache.ts, currency, since, tz);
    if (playsEl && cache.plays) playsEl.innerHTML = renderPlays(cache.plays, tz);
  }

  async function run(): Promise<void> {
    const c = readControls();
    if (c.error) {
      msgEl.textContent = c.error;
      msgEl.className = 'error filter-msg';
      return;
    }
    msgEl.textContent = 'Running…';
    msgEl.className = 'muted filter-msg';
    setLoading();

    // Effective current range + the matching prior window for deltas.
    const durMs = c.from && c.to ? new Date(c.to).getTime() - new Date(c.from).getTime() : SINCE_MS[c.since];
    const curEnd = c.to ? new Date(c.to).getTime() : Date.now();
    const curStart = c.from ? new Date(c.from).getTime() : curEnd - durMs;
    const priorFrom = new Date(curStart - durMs).toISOString();
    const priorTo = new Date(curStart).toISOString();
    // The basis is stated here, not just in the dropdown, because a figure
    // copied off this page carries no other record of which clock produced it.
    rangeEl.innerHTML = `${escapeHtml(SINCE_LABEL[c.since])} · ${escapeHtml(fmtDateTime(new Date(curStart).toISOString(), c.tz))} → ${escapeHtml(fmtDateTime(new Date(curEnd).toISOString(), c.tz))}${c.gameId ? ` · ${escapeHtml(c.gameId)}` : ' · all games'} · ${escapeHtml(c.tz)} · counted by <strong>${escapeHtml(BASIS_LABEL[c.basis].toLowerCase())}</strong><br><span class="muted">${escapeHtml(BASIS_HINT[c.basis])}</span>`;

    // Absolute window bounds — also what the P/L Plays fetch below uses, so
    // the on-screen bet list lines up exactly with the labeled range above
    // instead of re-deriving it from a "since" preset server-side.
    const curFromIso = new Date(curStart).toISOString();
    const curToIso = new Date(curEnd).toISOString();

    lastArgs = { gameId: c.gameId, since: c.since, from: c.from, to: c.to, player: c.player, currency, basis: c.basis, q: c.q };
    try {
      const tsSince: '24h' | '7d' | '30d' = c.since === '1h' ? '24h' : c.since;
      const [ggr, priorGgr, perGame, ts, plays] = await Promise.all([
        // Player search scopes KPIs + per-game to that player too (both the
        // current-window ggr call AND the prior-window one below, so the "vs
        // prev" delta chips stay player-scoped rather than reverting to the
        // whole integrator). `currency` scopes every metric to one currency —
        // turnover/GGR/RTP are meaningless summed across currencies.
        isPl ? deps.client.fetchGgrReport(deps.integratorId, { gameId: c.gameId, since: c.since, from: c.from, to: c.to, player: c.player, currency, basis: c.basis }) : Promise.resolve(null),
        isPl ? deps.client.fetchGgrReport(deps.integratorId, { gameId: c.gameId, from: priorFrom, to: priorTo, player: c.player, currency, basis: c.basis }) : Promise.resolve(null),
        isPl ? deps.client.fetchPerGameReport(deps.integratorId, { since: c.since, from: c.from, to: c.to, player: c.player, currency, basis: c.basis }) : Promise.resolve(null),
        isPl ? deps.client.fetchTimeseries(deps.integratorId, { gameId: c.gameId, since: tsSince, from: c.from, to: c.to, tz: c.tz, currency, basis: c.basis }) : Promise.resolve(null),
        // Player search narrows this to one player's plays; empty search shows
        // the window's most recent 200 plays — the bet list an operator
        // usually wants right on the report, without a separate export.
        isPl ? deps.client.fetchPlays(deps.integratorId, { gameId: c.gameId, from: curFromIso, to: curToIso, player: c.player, currency, limit: 200, basis: c.basis, q: c.q }) : Promise.resolve(null),
      ]);
      cache = {
        ggr: ggr as GgrReport | null,
        priorGgr: priorGgr as GgrReport | null,
        perGame: perGame as PerGameReport | null,
        ts: ts as TimeseriesReport | null,
        plays: plays as PlaysReport | null,
        gameId: c.gameId,
      };
      const ctxBase = { currency: currency ?? '', integratorId: deps.integratorId, client: deps.client, tz: c.tz };
      const a = lastArgs;
      if (isPl) repaintPl();
      if (isLog && playsEl) mountRecentBets(playsEl, ctxBase, (page, limit) => deps.client.fetchRecentBets(deps.integratorId, { ...a, page, limit }));
      if (isLog && winsEl) mountRecentWins(winsEl, ctxBase, (page, limit) => deps.client.fetchRecentBigWins(deps.integratorId, { ...a, page, limit }));
      if (isLog && autoplayEl) mountAutoplay(autoplayEl, ctxBase, (page, limit) => deps.client.fetchAutoPlaySessions(deps.integratorId, { since: a.since, page, limit }));
      msgEl.textContent = '';
    } catch (err) {
      const m = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
      msgEl.textContent = m;
      msgEl.className = 'error filter-msg';
      [perGameEl, bettorsEl, timeseriesEl, winsEl, autoplayEl, kpisEl, playsEl].forEach((el) => {
        if (el) el.innerHTML = `<p class="error">${escapeHtml(m)}</p>`;
      });
    }
  }

  function setLoading(): void {
    if (kpisEl) kpisEl.innerHTML = '';
    if (perGameEl) perGameEl.innerHTML = skeletonTable({ rows: 3, columns: 6 });
    if (bettorsEl) bettorsEl.innerHTML = skeletonTable({ rows: 5, columns: 6 });
    if (timeseriesEl) timeseriesEl.innerHTML = skeletonTable({ rows: 4, columns: 4 });
    if (playsEl) playsEl.innerHTML = skeletonTable({ rows: 8, columns: isPl ? 13 : 12 });
    if (winsEl) winsEl.innerHTML = skeletonTable({ rows: 6, columns: 10 });
    if (autoplayEl) autoplayEl.innerHTML = skeletonTable({ rows: 5, columns: 9 });
  }

  // ── Events ─────────────────────────────────────────────────────────────────
  root.querySelector<HTMLButtonElement>('#r-run')!.addEventListener('click', () => void run());
  root.querySelector<HTMLSelectElement>('#r-game')!.addEventListener('change', () => void run());
  // Player search — Enter re-runs immediately; Refresh also picks up the
  // current field value since readControls() reads it fresh each call.
  root.querySelector<HTMLInputElement>('#r-player')?.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') void run();
  });
  // Transaction-id search — Enter re-runs. The usual gesture here is paste,
  // then Enter, so no debounce-on-input: a half-pasted id is not a query.
  root.querySelector<HTMLInputElement>('#r-q')?.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') void run();
  });
  root.querySelector<HTMLSelectElement>('#r-tz')!.addEventListener('change', (ev) => {
    localStorage.setItem(REPORT_TZ_STORAGE_KEY, (ev.target as HTMLSelectElement).value);
    void run();
  });
  root.querySelector<HTMLSelectElement>('#r-basis')?.addEventListener('change', (ev) => {
    localStorage.setItem(REPORT_BASIS_STORAGE_KEY, (ev.target as HTMLSelectElement).value);
    void run();
  });
  // datetime-local wants a LOCAL "YYYY-MM-DDTHH:mm:ss" string (no timezone, no
  // trailing Z). Slice the local ISO-ish value off a Date via its parts.
  const toLocalInput = (d: Date): string => {
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
  };

  const fromEl = root.querySelector<HTMLInputElement>('#r-from')!;
  const toEl = root.querySelector<HTMLInputElement>('#r-to')!;

  root.querySelector<HTMLSelectElement>('#r-since')!.addEventListener('change', (ev) => {
    const custom = (ev.target as HTMLSelectElement).value === 'custom';
    root.querySelector<HTMLElement>('#r-custom-range')!.classList.toggle('hidden', !custom);
    if (custom) {
      // Prefill a sensible default so the fields aren't blank dd/mm/yyyy: today
      // 00:00:00 → now. The user can then narrow it (e.g. 00:00:00 → 23:59:59).
      if (!fromEl.value) {
        const start = new Date();
        start.setHours(0, 0, 0, 0);
        fromEl.value = toLocalInput(start);
      }
      if (!toEl.value) toEl.value = toLocalInput(new Date());
      fromEl.focus();
    } else {
      void run();
    }
  });
  // Currency switch re-runs the report (the whole thing is server-scoped to
  // one currency — client-side repaint isn't enough since the data itself
  // must be re-fetched for the new currency).
  root.querySelector<HTMLSelectElement>('#r-currency')?.addEventListener('change', (ev) => {
    currency = (ev.target as HTMLSelectElement).value || undefined;
    void run();
  });
  /**
   * P/L tab export — one workbook, 4 sheets (Overview, Per-game, Top
   * players, Plays). Plays is the primary ask: the individual bet ledger,
   * fetched fresh (capped server-side at 10k) rather than paged like the
   * on-screen tables. Honors the current player search so the exported
   * sheet matches whatever is narrowed on screen.
   */
  async function exportPlWorkbook(a: NonNullable<typeof lastArgs>, stamp: string): Promise<void> {
    const { tz } = readControls();

    // Resolve the absolute window the same way `run()` does when it labels
    // the range: `a.from`/`a.to` are only set for a custom range; for a
    // since-preset, mirror the now-minus-window math so the export lines up
    // with what's on screen.
    const durMs = a.from && a.to ? new Date(a.to).getTime() - new Date(a.from).getTime() : SINCE_MS[a.since];
    const curEnd = a.to ? new Date(a.to).getTime() : Date.now();
    const curStart = a.from ? new Date(a.from).getTime() : curEnd - durMs;
    const fromIso = new Date(curStart).toISOString();
    const toIso = new Date(curEnd).toISOString();

    showToast('Exporting P/L report — the plays sheet can run up to 10,000 rows…', 'info');

    const [bettors, playsReport] = await Promise.all([
      deps.client.fetchTopBettors(deps.integratorId, { ...a, page: 1, limit: 200 }),
      // Every filter the metrics apply must be forwarded here too, or the Plays
      // sheet lists bets the totals above it exclude.
      deps.client.fetchPlays(deps.integratorId, {
        gameId: a.gameId,
        from: fromIso,
        to: toIso,
        player: a.player,
        basis: a.basis,
        currency: a.currency,
      }),
    ]);

    // Overview — KPI summary (when the report has run) + filter context.
    const g = cache?.ggr;
    const overview: Array<{ Metric: string; Value: string | number }> = [
      { Metric: 'Integrator', Value: deps.integratorId },
      { Metric: 'Game', Value: a.gameId ?? 'All games' },
      { Metric: 'Window', Value: a.from && a.to ? 'Custom range' : SINCE_LABEL[a.since] },
      { Metric: 'Timezone', Value: tz },
      { Metric: 'From', Value: fmtDateTime(fromIso, tz) },
      { Metric: 'To', Value: fmtDateTime(toIso, tz) },
      // Without this the workbook is a set of numbers with no record of which
      // clock produced them, which is how two teams end up "disagreeing".
      { Metric: 'Counted by', Value: BASIS_LABEL[a.basis] },
    ];
    // Amounts are in the report's currency, as decimals ("1234.56" in USD).
    // With no currency selected the totals add up several currencies and have
    // no unit, so they stay the integer the API sent.
    const gCur = a.currency ?? soleCurrency(g);
    const amt = (v: string | number): string => (gCur ? toDecimalString(v, gCur) : String(v));
    if (g) {
      const arpu = avgCredits(g.houseTake, g.distinctPlayers);
      const avgBet = avgCredits(g.totalBet, g.betCount);
      overview.push(
        { Metric: 'Turnover', Value: amt(g.totalBet) },
        { Metric: 'Payout', Value: amt(g.totalPayout) },
        { Metric: 'GGR / house take', Value: amt(g.houseTake) },
        { Metric: 'RTP', Value: pct(g.rtp) },
        { Metric: 'Distinct players', Value: g.distinctPlayers },
        { Metric: 'Bet count', Value: g.betCount },
        { Metric: 'ARPU', Value: amt(arpu) },
        { Metric: 'Avg bet', Value: amt(avgBet) },
      );
    }
    overview.push({ Metric: 'Currency', Value: gCur ?? 'All currencies' });
    // Reconciles the Plays sheet against Bet count. The sheet lists the raw
    // ledger, the totals count realised bets, so the two differ by design;
    // stating both plus the gap removes the ambiguity.
    {
      const listed = playsReport.plays.length;
      const excluded = playsReport.plays.filter((p) => playsExclusionReason(p, a.basis) !== '').length;
      overview.push(
        { Metric: 'Plays rows listed', Value: listed },
        { Metric: 'Plays rows counted in totals', Value: listed - excluded },
        { Metric: 'Plays rows excluded', Value: excluded },
      );
    }

    const sheets: XlsxSheet[] = [
      { name: 'Overview', rows: overview, columns: ['Metric', 'Value'] },
      {
        name: 'Per-game',
        // Empty (header-only) when the per-game breakdown hasn't loaded yet.
        rows: (cache?.perGame?.games ?? []).map((gm) => {
          // This sheet lists every game, whatever the game filter, so each row
          // answers for its own currency: the selected one, else the only one
          // the game took bets in. A game played in several stays a blend.
          const rowCur = a.currency ?? (gm.byCurrency.length === 1 ? gm.byCurrency[0]!.currency : undefined);
          const rowAmt = (v: string): string => (rowCur ? toDecimalString(v, rowCur) : v);
          return {
            Game: gm.gameId,
            Currency: rowCur ?? 'All currencies',
            BetCount: gm.betCount,
            TotalBet: rowAmt(gm.totalBet),
            TotalPayout: rowAmt(gm.totalPayout),
            HouseTake: rowAmt(gm.houseTake),
            RTP: gm.rtp.toFixed(6),
          };
        }),
        columns: ['Game', 'Currency', 'BetCount', 'TotalBet', 'TotalPayout', 'HouseTake', 'RTP'],
      },
      {
        name: 'Top players',
        rows: bettors.entries.map((e) => ({
          Rank: e.rank,
          Player: e.displayName,
          Currency: e.currency,
          Bets: e.betCount,
          Turnover: toDecimalString(e.totalBet, e.currency),
          Net: toDecimalString(e.net, e.currency),
        })),
        columns: ['Rank', 'Player', 'Currency', 'Bets', 'Turnover', 'Net'],
      },
      {
        name: 'Plays',
        rows: playsReport.plays.map((p) => ({
          // First column: the id the integrator's own list prints, so the two
          // spreadsheets can be diffed on a key both sides share.
          TransactionId: p.transactionId,
          PlacedAt: fmtDateTime(p.placedAt, tz),
          SettledAt: p.settledAt ? fmtDateTime(p.settledAt, tz) : '',
          Player: p.username ?? `Player_${p.playerId.slice(0, 4)}`,
          Identity: p.integratorPlayerRef,
          Game: p.gameId,
          BetType: p.betType,
          Outcome: p.outcome,
          AcceptStatus: p.acceptStatus,
          Currency: p.currency,
          Stake: toDecimalString(p.creditAmount, p.currency),
          Payout: toDecimalString(p.creditPayout, p.currency),
          // The totals count realised bets only. A listed row that is not
          // counted carries the reason here, so a row count that differs from
          // Bet count is answerable from the sheet itself.
          CountedInTotals: playsExclusionReason(p, a.basis) === '' ? 'yes' : 'no',
          ExcludedBecause: playsExclusionReason(p, a.basis),
          RoundId: p.roundId,
          BetId: p.betId,
        })),
        columns: [
          'TransactionId',
          'PlacedAt',
          'SettledAt',
          'Player',
          'Identity',
          'Game',
          'BetType',
          'Outcome',
          'AcceptStatus',
          'Currency',
          'Stake',
          'Payout',
          'CountedInTotals',
          'ExcludedBecause',
          'RoundId',
          'BetId',
        ],
      },
    ];

    exportXlsxWorkbook(`apx-pl-report-${stamp}.xlsx`, sheets);

    if (playsReport.capped) {
      showToast(
        `Plays sheet capped at ${playsReport.count.toLocaleString()} rows — narrow the window for a complete export.`,
        'warn',
      );
    } else {
      showToast('P/L report exported.', 'success');
    }
  }

  async function exportData(): Promise<void> {
    if (!lastArgs) {
      showToast('Run the report first.', 'warn');
      return;
    }
    const a = lastArgs;
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');

    if (isPl) {
      await exportPlWorkbook(a, stamp);
      return;
    }

    // Other tabs (game-log, …) — unchanged per-section file exports. Pull a
    // generous batch for the paged lists so the export isn't limited to the
    // on-screen page.
    const [wins, autoplay] = await Promise.all([
      isLog ? deps.client.fetchRecentBigWins(deps.integratorId, { ...a, page: 1, limit: 200 }) : Promise.resolve(null),
      isLog ? deps.client.fetchAutoPlaySessions(deps.integratorId, { since: a.since, page: 1, limit: 200 }) : Promise.resolve(null),
    ]);
    let count = 0;
    if (wins) {
      // Amounts as decimals in the bet's own currency ("1234.56" in USD).
      exportXlsx(`apx-${tab}-${stamp}-recent-wins.xlsx`, wins.wins.map((w) => ({ settledAt: w.settledAt ?? '', playerId: w.playerId, displayName: w.displayName, gameId: w.gameId, betType: w.betType, currency: w.currency, creditAmount: toDecimalString(w.creditAmount, w.currency), creditPayout: toDecimalString(w.creditPayout, w.currency), roundId: w.roundId, betId: w.betId })));
      count++;
    }
    if (autoplay) {
      exportXlsx(`apx-${tab}-${stamp}-autoplay.xlsx`, autoplay.sessions.map((s) => ({ sessionId: s.sessionId, playerId: s.playerId, displayName: s.displayName, target: s.target, rounds: s.rounds, currency: s.currency, totalStaked: toDecimalString(s.totalStaked, s.currency), totalPayout: toDecimalString(s.totalPayout, s.currency), net: toDecimalString(s.net, s.currency), firstAt: s.firstAt, lastAt: s.lastAt })));
      count++;
    }
    showToast(count === 0 ? 'Nothing to export for this tab.' : `${count} CSV${count > 1 ? 's' : ''} downloaded.`, count === 0 ? 'warn' : 'success');
  }

  root.querySelector<HTMLButtonElement>('#r-export')!.addEventListener('click', () => void exportData());

  await run();
}

async function loadGames(deps: ReportsPageDeps): Promise<string[]> {
  try {
    const { games } = await deps.client.listGameCatalog(deps.integratorId);
    return games.map((g) => g.gameId).sort((a, b) => a.localeCompare(b));
  } catch {
    return [];
  }
}

/** Enabled currencies this integrator trades in — powers the report currency
 *  filter. Fail-soft to empty → the report runs unfiltered (blended). */
async function loadCurrencies(deps: ReportsPageDeps): Promise<string[]> {
  try {
    const rows = await deps.client.listIntegratorFiatCurrencies(deps.integratorId);
    return rows
      .filter((r) => r.enabled)
      .map((r) => r.fiatCurrency)
      .sort((a, b) => a.localeCompare(b));
  } catch {
    return [];
  }
}

/**
 * The one currency an unfiltered report holds, when it holds exactly one — an
 * integrator with no enabled currency still runs the report unfiltered, and its
 * figures are then that single currency's. Undefined for a blend of several.
 */
function soleCurrency(g: GgrReport | null | undefined): string | undefined {
  return g && g.byCurrency.length === 1 ? g.byCurrency[0]!.currency : undefined;
}

function renderKpis(g: GgrReport, prior: GgrReport | null, ccy: string | undefined): string {
  // Every figure is already scoped to one currency by the server filter, so the
  // credit-native totals ARE the currency amounts — label them, don't blend.
  const bet = g.totalBet;
  const payout = g.totalPayout;
  const house = g.houseTake;
  const arpu = avgCredits(house, g.distinctPlayers);
  const avgBet = avgCredits(bet, g.betCount);

  const pBet = prior ? Number(prior.totalBet) : NaN;
  const pPayout = prior ? Number(prior.totalPayout) : NaN;
  const pHouse = prior ? Number(prior.houseTake) : NaN;
  const pPlayers = prior ? prior.distinctPlayers : NaN;

  const houseClass = Number(house) >= 0 ? 'pos' : 'neg';

  const card = (
    label: string,
    value: string,
    opts: { delta?: string; valueClass?: string; sub?: string } = {},
  ): string =>
    `<div class="stat-card">
      <div class="stat-card-label">${label}</div>
      <div class="stat-card-value ${opts.valueClass ?? ''}">${value}</div>
      ${opts.delta ? `<div class="stat-card-delta">${opts.delta}</div>` : ''}
      ${opts.sub ?? ''}
    </div>`;

  // "All currencies" renders one strip per currency rather than one blended
  // strip: money totals are only meaningful within a single currency. Matches
  // how the provider by-integrator report presents the same split.
  if (!ccy && g.byCurrency.length > 1) {
    const priorByCcy = new Map((prior?.byCurrency ?? []).map((c) => [c.currency, c]));
    const strips = [...g.byCurrency]
      .sort((a, b) => Number(b.totalBet) - Number(a.totalBet))
      .map((c) => {
        const p = priorByCcy.get(c.currency);
        return (
          `<div class="stat-group-label"><span class="chip">${escapeHtml(c.currency)}</span></div>` +
          '<div class="players-stats">' +
          [
            card(`Turnover (${c.currency})`, money(c.totalBet, c.currency), {
              delta: deltaChip(Number(c.totalBet), p ? Number(p.totalBet) : NaN),
              sub: `<div class="stat-card-sub">${c.betCount.toLocaleString()} bets</div>`,
            }),
            card(`Payout (${c.currency})`, money(c.totalPayout, c.currency), {
              delta: deltaChip(Number(c.totalPayout), p ? Number(p.totalPayout) : NaN, { neutral: true }),
            }),
            card(`GGR / house take (${c.currency})`, money(c.houseTake, c.currency), {
              delta: deltaChip(Number(c.houseTake), p ? Number(p.houseTake) : NaN),
              valueClass: Number(c.houseTake) >= 0 ? 'pos' : 'neg',
            }),
            card('Empirical RTP', pct(c.rtp), {
              delta: p ? deltaChip(c.rtp, p.rtp, { neutral: true }) : undefined,
              sub: `<div class="stat-card-sub">hold ${pct(1 - c.rtp)}</div>`,
            }),
            card(`Avg bet (${c.currency})`, money(String(avgCredits(c.totalBet, c.betCount)), c.currency), {
              sub: `<div class="stat-card-sub">turnover ÷ bets</div>`,
            }),
          ].join('') +
          '</div>'
        );
      })
      .join('');
    // Players and total bets are counts, not money — they blend cleanly, so
    // they stay a single figure rather than being repeated per currency.
    const shared =
      '<div class="players-stats">' +
      [
        card('Distinct players', g.distinctPlayers.toLocaleString(), {
          delta: deltaChip(g.distinctPlayers, pPlayers),
          sub: `<div class="stat-card-sub">across all currencies</div>`,
        }),
        card('Total bets', g.betCount.toLocaleString(), {
          sub: `<div class="stat-card-sub">across all currencies</div>`,
        }),
      ].join('') +
      '</div>';
    return strips + shared;
  }

  return '<div class="players-stats">' + [
    card(`Turnover${ccy ? ` (${ccy})` : ''}`, money(bet, ccy), {
      delta: deltaChip(Number(bet), pBet),
      sub: `<div class="stat-card-sub">${g.betCount.toLocaleString()} bets</div>`,
    }),
    card(`Payout${ccy ? ` (${ccy})` : ''}`, money(payout, ccy), {
      delta: deltaChip(Number(payout), pPayout, { neutral: true }),
    }),
    card(`GGR / house take${ccy ? ` (${ccy})` : ''}`, money(house, ccy), {
      delta: deltaChip(Number(house), pHouse),
      valueClass: houseClass,
    }),
    card('Empirical RTP', pct(g.rtp), {
      delta: prior ? deltaChip(g.rtp, prior.rtp, { neutral: true }) : undefined,
      sub: `<div class="stat-card-sub">hold ${pct(1 - g.rtp)}</div>`,
    }),
    card('Distinct players', g.distinctPlayers.toLocaleString(), {
      delta: deltaChip(g.distinctPlayers, pPlayers),
    }),
    card(`ARPU${ccy ? ` (${ccy})` : ''}`, money(String(arpu), ccy), {
      sub: `<div class="stat-card-sub">GGR ÷ players</div>`,
    }),
    card(`Avg bet${ccy ? ` (${ccy})` : ''}`, money(String(avgBet), ccy), {
      sub: `<div class="stat-card-sub">turnover ÷ bets</div>`,
    }),
  ].join('') + '</div>';
}

/**
 * Why a listed play is not in the totals, or '' when it is.
 *
 * The Plays sheet is the raw ledger and lists every bet in the window; the
 * totals count realised bets only (`outcome IN (WIN, LOSE)`) and, under the
 * settled basis, only those that have settled. Stating the reason per row keeps
 * a row count that differs from Bet count self-explaining — the sheet's
 * AcceptStatus column then says which flavour of excluded row it was (a
 * `cancelled` VOID is the player's own UNDO, a `confirmed` one an admin
 * refund; a `rejected` PENDING is a debit that never landed).
 */
export function playsExclusionReason(
  p: { outcome: string; settledAt: string | null },
  basis: ReportBasis,
): string {
  if (p.outcome === 'VOID') return 'cancelled bet — stake refunded, not turnover';
  if (p.outcome === 'PENDING') return 'not settled yet';
  if (basis === 'settled' && !p.settledAt) return 'no settle time';
  return '';
}

function renderTimeseries(r: TimeseriesReport, ccy: string | undefined, since: Since, tz?: string): string {
  const tooShort = since === '1h'
    ? '<p class="muted section-desc">Hourly window is too short for a trend — showing the last 24h.</p>'
    : '';
  if (r.points.length === 0) return `${tooShort}<p class="muted">No activity in this window.</p>`;
  // Unfiltered, the buckets are still one currency's when only one appears.
  if (!ccy) {
    const seen = new Set(r.points.flatMap((p) => p.byCurrency.map((c) => c.currency)));
    if (seen.size === 1) ccy = [...seen][0];
  }
  const maxBet = Math.max(...r.points.map((p) => Number(p.totalBet))) || 1;
  const colHdr = ccy ? `Total bet (${ccy})` : 'Total bet';
  // Single currency now (server-scoped) — bucket totals are that currency's.
  return `${tooShort}<p class="muted section-desc">Bucket <code>${r.bucket}</code> · ${r.points.length} points · blue = turnover, gold = payout (relative to window max).</p>
    <div class="ts-bars">
      <div class="muted">Time</div>
      <div class="muted">Turnover / payout</div>
      <div class="muted num">${colHdr}</div>
      <div class="muted num">House take</div>
      ${r.points
        .map((p) => {
          const betPct = (Number(p.totalBet) / maxBet) * 100;
          const payoutPct = (Number(p.totalPayout) / maxBet) * 100;
          const houseClass = Number(p.houseTake) >= 0 ? 'pos' : 'neg';
          const ts = r.bucket === 'hour' ? fmtTime(p.t, tz) : fmtDate(p.t, tz);
          return `
            <div><code>${ts}</code></div>
            <div class="ts-bar-track">
              <div class="ts-bar-bet" style="width:${betPct.toFixed(1)}%"></div>
              <div class="ts-bar-payout" style="width:${payoutPct.toFixed(1)}%"></div>
            </div>
            <div class="num">${money(p.totalBet, ccy)}</div>
            <div class="num ${houseClass}">${money(p.houseTake, ccy)}</div>
          `;
        })
        .join('')}
    </div>`;
}

/**
 * P/L "Plays" card — flat bet list backing the on-screen drill-down (and,
 * when exported, the workbook's Plays sheet). Unlike the paged `mountX`
 * tables in reports-tables.ts, this is one already-fetched capped batch
 * (`fetchPlays`), so it renders straight from the report with no pager.
 */
function renderPlays(r: PlaysReport, tz?: string): string {
  if (r.plays.length === 0) return '<p class="muted">No plays match this window.</p>';
  const capNote = r.capped
    ? `<p class="muted section-desc">Showing first ${r.count.toLocaleString()} — export for the full list.</p>`
    : '';
  return `${capNote}<div class="data-table-wrap"><table class="data-table">
    <thead><tr>
      <th>Transaction ID</th><th>Placed</th><th>Settled</th><th>Player</th><th>Identity</th><th>Game</th><th>Bet type</th><th>Currency</th>
      <th>Outcome</th><th>Accept status</th><th class="num">Stake</th><th class="num">Payout</th><th>Round</th>
    </tr></thead>
    <tbody>${r.plays
      .map((p) => {
        const outcomeClass = p.outcome === 'WIN' ? 'live' : p.outcome === 'VOID' ? 'warn' : '';
        return `<tr>
          ${txIdCell(p.transactionId)}
          <td>${fmtDateTime(p.placedAt, tz)}</td>
          <td>${p.settledAt ? fmtDateTime(p.settledAt, tz) : '<span class="muted">unsettled</span>'}</td>
          <td>${escapeHtml(p.username ?? p.playerId)}</td>
          <td><code>${escapeHtml(p.integratorPlayerRef)}</code></td>
          <td><code>${escapeHtml(p.gameId)}</code></td>
          <td><code>${escapeHtml(p.betType)}</code></td>
          <td><code>${escapeHtml(p.currency)}</code></td>
          <td><span class="pill ${outcomeClass}">${escapeHtml(p.outcome)}</span></td>
          ${acceptStatusCell(p.acceptStatus)}
          <td class="num">${fmtMoney(p.creditAmount, p.currency)}</td>
          <td class="num">${fmtMoney(p.creditPayout, p.currency)}</td>
          <td><code>${escapeHtml(p.roundId.slice(-8))}</code></td>
        </tr>`;
      })
      .join('')}</tbody>
  </table></div>`;
}
